import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { adminManagementService } from '@/admin/services/adminManagement';
import { UserStatisticsRequest, UserStatisticsResponse } from '@/school-management/types/response';

export type UserStatisticsData = UserStatisticsResponse['data'];

interface UseUserStatisticsReturn {
  data: UserStatisticsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function getPayloadKey(payload?: UserStatisticsRequest): string {
  const p = payload ?? {};
  const keys = Object.keys(p).sort();
  const sorted = keys.reduce((acc, k) => ({ ...acc, [k]: (p as Record<string, unknown>)[k] }), {});
  return JSON.stringify(sorted);
}

/**
 * Fetches user statistics from POST /admin-management/{version}/metrics/user-statistics.
 * Backend derives organization from token.
 */
export const useUserStatistics = (
  enabled: boolean = true,
  payload?: UserStatisticsRequest
): UseUserStatisticsReturn => {
  const [data, setData] = useState<UserStatisticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const payloadKey = useMemo(() => getPayloadKey(payload), [payload]);

  const fetchStats = useCallback(async () => {
    if (!enabled || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const currentPayload = payloadRef.current ?? {};
      const response = await adminManagementService.getUserStatistics(currentPayload);

      if (response?.data?.data) {
        setData(response.data.data);
      } else {
        setError('No data received from server');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching user statistics:', err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [enabled, payloadKey]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refetch = useCallback(() => {
    isFetchingRef.current = false;
    fetchStats();
  }, [fetchStats]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};
